import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  UserGroupIcon,
  ClockIcon,
  TagIcon,
  ChatBubbleLeftRightIcon,
  HeartIcon,
  StarIcon,
  ShareIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [isStarred, setIsStarred] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [starCount, setStarCount] = useState(0);
  const { user: currentUser } = useAuthStore();
  const [contactLoading, setContactLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch project details
  const fetchProject = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.projects.getById(id);
      setProject(response);
      setLikeCount(response.likes_count || 0);
      setStarCount(response.stars_count || 0);
    } catch (err) {
      console.error('Error fetching project:', err);
      setError('Failed to load project details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch project on component mount
  useEffect(() => {
    fetchProject();
  }, [id]);

  // Handle like project
  const handleLike = async () => {
    try {
      await api.projects.like(id);
      setIsLiked(!isLiked);
      setLikeCount(prev => isLiked ? prev - 1 : prev + 1);
    } catch (err) {
      console.error('Error liking project:', err);
    }
  };

  // Handle star project
  const handleStar = async () => {
    try {
      if (isStarred) {
        await api.projects.unstar(id);
      } else {
        await api.projects.star(id);
      }
      setIsStarred(!isStarred);
      setStarCount(prev => isStarred ? prev - 1 : prev + 1);
    } catch (err) {
      console.error('Error starring project:', err);
    }
  };

  // Handle share project
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: project?.title,
        text: project?.description,
        url: window.location.href,
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      // You could add a toast notification here
    }
  };

  // Contact Team logic
  const handleContactTeam = async () => {
    if (!currentUser?.id) {
      alert('Please log in to contact the team.');
      return;
    }
    if (!project?.creator_id) {
      alert('No creator found for this project.');
      return;
    }
    setContactLoading(true);
    try {
      const chat = await api.messages.createChat({
        name: project.creator_name,
        is_group: false,
        participant_ids: [currentUser.id, project.creator_id],
      });
      navigate(`/messages/${chat.id}`);
    } catch (err) {
      alert('Failed to create chat. Please try again.');
    } finally {
      setContactLoading(false);
    }
  };

  const isCreator = currentUser && project && project.creator_id === currentUser.id;

  const handleEditProject = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await api.projects.update(project.id, {
        title: editProject.title,
        description: editProject.description,
        category: editProject.category,
        tech_stack: editProject.tech_stack.split(',').map(t => t.trim()).filter(Boolean),
        team_name: editProject.team_name,
        looking_for: editProject.looking_for,
        image_url: editProject.image_url,
      });
      setShowEditModal(false);
      // Refresh project details
      const data = await api.projects.getById(project.id);
      setProject(data);
    } catch (err) {
      alert('Failed to update project.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) return;
    setDeleteLoading(true);
    try {
      await api.projects.delete(project.id);
      navigate('/discover');
    } catch (err) {
      alert('Failed to delete project.');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">
              {error || 'Project not found'}
            </p>
            <button
              onClick={() => navigate('/discover')}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Back to Projects
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent relative">
      {/* Noise Texture Overlay */}
      <div className="absolute inset-0 opacity-[0.015] mix-blend-soft-light pointer-events-none">
        <svg className="w-full h-full">
          <filter id="noise">
            <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
            <feColorMatrix type="saturate" values="0" />
          </filter>
          <rect width="100%" height="100%" filter="url(#noise)" />
        </svg>
      </div>

      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-100/40 via-purple-100/40 to-pink-100/40 dark:from-indigo-900/20 dark:via-purple-900/20 dark:to-pink-900/20" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-indigo-200/30 via-transparent to-transparent dark:from-indigo-800/20" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate('/discover')}
          className="inline-flex items-center text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 mb-8"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Projects
        </button>

        {/* Project Header */}
        <div className="bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm rounded-xl p-8 mb-8 border border-gray-200/20 dark:border-gray-700/20">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{project.title}</h1>
              <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-300">
                <span className="flex items-center">
                  <TagIcon className="h-4 w-4 mr-1" />
                  {project.category}
                </span>
                {project.created_at && (
                <span className="flex items-center">
                  <ClockIcon className="h-4 w-4 mr-1" />
                    Created {new Date(project.created_at).toLocaleDateString()}
                </span>
                )}
                {project.team_name && (
                <span className="flex items-center">
                  <UserGroupIcon className="h-4 w-4 mr-1" />
                    {project.team_name}
                </span>
                )}
              </div>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={handleLike}
                className={`p-2 rounded-lg transition-colors ${
                  isLiked
                    ? 'text-red-500 bg-red-50 dark:bg-red-900/20'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                }`}
              >
                <HeartIcon className="h-6 w-6" />
              </button>
              <button
                onClick={handleStar}
                className={`p-2 rounded-lg transition-colors ${
                  isStarred
                    ? 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                }`}
              >
                <StarIcon className="h-6 w-6" />
              </button>
              <button 
                onClick={handleShare}
                className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50"
              >
                <ShareIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Project Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <div className="bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm rounded-xl p-8 border border-gray-200/20 dark:border-gray-700/20">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Description</h2>
              <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{project.description}</p>
            </div>

            {/* Technologies */}
            {project.tech_stack && project.tech_stack.length > 0 && (
            <div className="bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm rounded-xl p-8 border border-gray-200/20 dark:border-gray-700/20">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Technologies</h2>
              <div className="flex flex-wrap gap-2">
                  {project.tech_stack.map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-sm"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
            )}

            {/* Looking For */}
            {project.looking_for && (
            <div className="bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm rounded-xl p-8 border border-gray-200/20 dark:border-gray-700/20">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Looking For</h2>
                <div className="flex flex-wrap gap-2">
                  {project.looking_for.split(',').map((role) => (
                    <span
                      key={role.trim()}
                      className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm"
                    >
                      {role.trim()}
                    </span>
                  ))}
                </div>
                      </div>
            )}

            {/* Project Files */}
            {project.files && project.files.length > 0 && (
              <div className="bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm rounded-xl p-8 border border-gray-200/20 dark:border-gray-700/20">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Project Files</h2>
                <div className="space-y-2">
                  {project.files.map((file) => (
                    <div key={file} className="flex items-center justify-between p-3 bg-white/50 dark:bg-gray-700/50 rounded-lg">
                      <span className="text-gray-700 dark:text-gray-300">{file}</span>
                      <a
                        href={`${process.env.REACT_APP_API_URL}/api/v1/projects/${project.id}/files/${file}`}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                      >
                        Download
                      </a>
                  </div>
                ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Team and Stats */}
          <div className="space-y-8">
            {/* Project Stats */}
            <div className="bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm rounded-xl p-8 border border-gray-200/20 dark:border-gray-700/20">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Project Stats</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-white/50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{likeCount}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Likes</p>
                </div>
                <div className="text-center p-4 bg-white/50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{starCount}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Stars</p>
                </div>
              </div>
            </div>

            {/* Project Info */}
            <div className="bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm rounded-xl p-8 border border-gray-200/20 dark:border-gray-700/20">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Project Info</h2>
              <div className="space-y-3">
                {project.status && (
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">{project.status}</span>
                  </div>
                )}
                {project.category && (
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Category:</span>
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-400 capitalize">
                      {project.category.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </span>
                  </div>
                )}
                {project.team_name && (
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Team:</span>
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">{project.team_name}</span>
                  </div>
                )}
                {project.creator_name && (
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Creator:</span>
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">{project.creator_name}</span>
                  </div>
                )}
                {project.created_at && (
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Created:</span>
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                      {new Date(project.created_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {project.updated_at && (
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Updated:</span>
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                      {new Date(project.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Contact/Join */}
            <div className="bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm rounded-xl p-8 border border-gray-200/20 dark:border-gray-700/20">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Get Involved</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Interested in this project? Reach out to the team or apply to join!
              </p>
              <div className="space-y-3">
                <button
                  className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  onClick={handleContactTeam}
                  disabled={contactLoading}
                >
                  {contactLoading ? 'Creating chat...' : 'Contact Team'}
                </button>
                {/* <button className="w-full px-4 py-2 border border-indigo-600 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors">
                  Apply to Join
                </button> */}
              </div>
            </div>
          </div>
        </div>

        {isCreator && (
          <div className="flex space-x-2 mb-4">
            <button
              onClick={() => {
                setEditProject({
                  title: project.title || '',
                  description: project.description || '',
                  category: project.category || '',
                  tech_stack: Array.isArray(project.tech_stack) ? project.tech_stack.join(', ') : '',
                  team_name: project.team_name || '',
                  looking_for: project.looking_for || '',
                  image_url: project.image_url || '',
                });
                setShowEditModal(true);
              }}
              className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
            >
              Edit
            </button>
            <button
              onClick={handleDeleteProject}
              disabled={deleteLoading}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        )}
      </div>

      {/* Edit Project Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit Project</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleEditProject} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                <input
                  type="text"
                  value={editProject.title}
                  onChange={e => setEditProject(prev => ({ ...prev, title: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  value={editProject.description}
                  onChange={e => setEditProject(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                <input
                  type="text"
                  value={editProject.category}
                  onChange={e => setEditProject(prev => ({ ...prev, category: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tech Stack (comma separated)</label>
                <input
                  type="text"
                  value={editProject.tech_stack}
                  onChange={e => setEditProject(prev => ({ ...prev, tech_stack: e.target.value }))}
                  placeholder="React, Node.js, MongoDB"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Team Name</label>
                <input
                  type="text"
                  value={editProject.team_name}
                  onChange={e => setEditProject(prev => ({ ...prev, team_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Looking For</label>
                <input
                  type="text"
                  value={editProject.looking_for}
                  onChange={e => setEditProject(prev => ({ ...prev, looking_for: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Image URL</label>
                <input
                  type="text"
                  value={editProject.image_url}
                  onChange={e => setEditProject(prev => ({ ...prev, image_url: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editLoading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetails; 